import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { dayjs } from '@karve.it/core';
import { capitalize } from 'lodash';

import { FullCalendarEventFragment } from '../../../generated/graphql.generated';
import { EventAttendeeRoles, JOB_ROLE_MAP, locationNameMap } from '../../global.constants';
import { strToTitleCase } from '../../js';
import { S_ONE_HOUR, timeInSpan } from '../../time';
import { formatUserName } from '../../users/users.utils';
import { CrewListComponent } from '../crew-list/crew-list.component';
import { EventColorInfo } from '../schedule.interfaces';
import { showWelcomeCallIndicator } from '../schedule.util';

@Component({
  selector: 'app-schedule-event-hover',
  templateUrl: './schedule-event-hover.component.html',
  styleUrls: ['./schedule-event-hover.component.scss'],
  standalone: true,
  imports: [CommonModule, CrewListComponent]
})
export class ScheduleEventHoverComponent implements OnInit {
  @Input() event: FullCalendarEventFragment;
  @Input() title: string;
  @Input() customerName: string;
  @Input() colors: EventColorInfo;
  @Input() timezone: string;
  @Input() currentViewStart: Date;
  @Input() currentViewEnd: Date;
  @Input() locations: any[]; // processed locations
  @Input() statusIcon: string;
  @Input() eventStart: number;
  @Input() eventEnd: number;
  @Input() width: number;
  @Input() crewSize: number;

  ngOnInit() {
  }

  get status() {
    return capitalize(this.event?.status);
  }

  get showWelcomeCall() {
    return showWelcomeCallIndicator(this.event);
  }

  get jobCode() {
    return this.event?.job?.code;
  }

  get formattedTime() {
    if (!this.eventStart || !this.eventEnd) return '';

    const baseTime = 'h:mm a';
    const dateTime = 'MMM D h:mm a';
    
    const calendarStart = dayjs(this.currentViewStart);
    const calendarEnd = dayjs(this.currentViewEnd);
    
    const start = dayjs.tz(this.eventStart * 1000, this.timezone);
    const end = dayjs.tz(this.eventEnd * 1000, this.timezone);
    
    let startFormat = baseTime;
    let endFormat = baseTime;

    if (!timeInSpan(start.unix(), calendarStart.unix(), calendarEnd.unix())) {
      startFormat = dateTime;
    }
    if (!timeInSpan(end.unix(), calendarStart.unix(), calendarEnd.unix())) {
      endFormat = dateTime;
    }

    const showEnd = (end.unix() - start.unix()) > S_ONE_HOUR;

    let innerText = `${ start.format(startFormat) }`;
    if (showEnd) {
      innerText += ` to ${ end.format(endFormat) }`;
    }
    return innerText;
  }

  get assetNames() {
    if (!this.event?.assets) return '';
    return this.event.assets.map(a => a.name).join(', ');
  }

  get headerTitle() {
    const parts = [];
    if (this.assetNames) parts.push(this.assetNames);
    
    const type = strToTitleCase(this.event.type || '');
    if (type) parts.push(type);
    
    if (this.title && this.title.toLowerCase() !== type.toLowerCase()) {
        parts.push(this.title);
    }
    
    return parts.join(' - ');
  }

  get attendees() {
    if (!this.event) return [];
    const customer = this.event.job?.users?.find((a) => a.role === JOB_ROLE_MAP.customerRole);
    
    const crewRoles: string[] = [
        EventAttendeeRoles.crewLead,
        EventAttendeeRoles.assistantCrewLead,
        EventAttendeeRoles.crewMember,
        EventAttendeeRoles.driver
    ];
    // Filter out crew and other non-display roles
    const excludedRoles = [...crewRoles, 'Author', 'BookedBy'];
    
    const allAttendees = [customer, ...(this.event.attendees || [])];
    const filteredAttendees = allAttendees.filter(a => a && !excludedRoles.includes(a.role as any));
    
    // Deduplicate
    const uniqueMap = new Map();
    filteredAttendees.forEach((a, index) => {
        const id = a.user?.id || `__noid__${index}`;
        if (!uniqueMap.has(id)) {
            uniqueMap.set(id, a);
        }
    });
    
    return Array.from(uniqueMap.values());
  }

  get crewAttendees() {
    if (!this.event?.attendees) return [];
    const crewRoles: string[] = [
        EventAttendeeRoles.crewLead,
        EventAttendeeRoles.assistantCrewLead,
        EventAttendeeRoles.crewMember,
        EventAttendeeRoles.driver
    ];
    const crew = this.event.attendees.filter(a => crewRoles.includes(a.role as any));

    // Deduplicate
    const uniqueMap = new Map();
    for (const c of crew) {
        const id = c.user?.id;
        if (id && !uniqueMap.has(id)) {
            uniqueMap.set(id, c);
        }
    }
    return Array.from(uniqueMap.values());
  }

  get paymentInfo() {
    if (!this.event?.job) return null;
    const total = this.event.job.total ?? 0;
    const paid = this.event.job.paidTotal ?? 0;
    const balance = total - paid;
    const discountedSubTotal = this.event.discountedSubTotal ?? 0;
    const currency = this.event.job.currency || 'USD';
    return {
      total,
      paid,
      balance,
      discountedSubTotal,
      currency
    };
  }

  get financials() {
    if (!this.event?.job) return null;
    const e = this.event as any;
    const currency = e.job.currency || 'USD';
    
    return {
      charges: e.subTotal ?? 0,
      discounts: e.discountTotal ?? e.job?.discountTotal ?? 0,
      discountedSubTotal: e.discountedSubTotal ?? 0,
      taxes: e.taxTotal ?? e.job?.taxTotal ?? 0,
      total: e.total ?? 0,
      paid: e.job?.paidTotal ?? 0,
      balance: (e.total ?? 0) - (e.job?.paidTotal ?? 0),
      currency
    };
  }

  get areaName() {
    if (!this.event) return 'Locations';
    return this.event.locations?.find((l) => l.type === 'start')?.location?.serviceArea?.name
      || (this.event.zones?.nodes?.[0] ? this.event.zones.nodes[0].name : 'Locations');
  }

  get product() {
    return this.colors?.leftDivColorProduct;
  }

  formatUserName(user) {
    return formatUserName(user);
  }

  formatRole(role) {
    return strToTitleCase(role);
  }

  getLocationType(loc) {
    return strToTitleCase(locationNameMap[loc.type] || loc.type);
  }

  formatLocationTime(time) {
    if (time === null || time === undefined || !Number.isFinite(time)) {
        return '';
    }
    return dayjs.tz(time * 1000, this.timezone).format('h:mm a');
  }

  getArrivalDeparture(loc) {
    const arrival = dayjs.tz(loc.arrivalTime * 1000, this.timezone);
    const departure = dayjs.tz(loc.departureTime * 1000, this.timezone);
    
    if (arrival.unix() === departure.unix()) {
      return arrival.format('h:mm a');
    }
    return `${arrival.format('h:mm a')} / ${departure.format('h:mm a')}`; // Or two cells
  }
  
  getTravelDuration(seconds) {
    return dayjs.duration(seconds, 'seconds').humanize();
  }
}
